import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import { useConfigurationContext } from 'src/provider/ConfigurationProvider';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';

const WORKFLOW_SUGGESTIONS = [
  {
    name: 'Escalate Sensitive Topics',
    steps:
      'When a customer requests a refund, subscription cancellation or account removal, escalate the conversation.',
  },
  {
    name: 'Bug Report',
    steps:
      'When a customer reports a bug, malfunction, or unexpected product behavior, apologize for the inconvenience and escalate the conversation.',
  },
  {
    name: 'Feature Request',
    steps:
      'When a customer suggests a new feature or enhancement to an existing product, thank them for the ideas, mention that you will pass the feedback on to the product team, and escalate the conversation.',
  },
];

export const EditWorkflowBlock = ({
  compact = false,
  onWorkflowAdd,
  onWorkflowsChange,
}: {
  compact?: boolean;
  onWorkflowAdd: (workflow: Workflow) => void;
  onWorkflowsChange: (workflow: Workflow, index: number) => void;
}) => {
  const { selectedWorkflow, setSelectedWorkflow, setIsEditWorkflowMode } =
    useConfigurationContext();
  const {
    clearErrors,
    trigger,
    control,
    getValues,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      enabled: selectedWorkflow ? Boolean(selectedWorkflow.enabled) : true,
      name: selectedWorkflow ? selectedWorkflow.name : '',
      trigger_value: selectedWorkflow ? selectedWorkflow.trigger_value : '',
      type: 'custom',
    },
    resolver: yupResolver(
      object({
        enabled: booleanScheme({ required: true }),
        name: stringScheme({ required: true }).label('Workflow name'),
        trigger_value: stringScheme({ required: true }).label('Workflow steps'),
        type: stringScheme({ required: true }).label('Type'),
      }),
    ),
  });

  useEffect(() => {
    setValue('enabled', selectedWorkflow ? selectedWorkflow.enabled : true, {
      shouldValidate: Boolean(selectedWorkflow),
    });
    setValue('name', selectedWorkflow ? selectedWorkflow.name : '', {
      shouldValidate: Boolean(selectedWorkflow),
    });
    setValue('trigger_value', selectedWorkflow ? selectedWorkflow.trigger_value : '', {
      shouldValidate: Boolean(selectedWorkflow),
    });
  }, [selectedWorkflow]);

  const onDiscard = () => {
    setIsEditWorkflowMode(false);
    setSelectedWorkflow(null);
  };

  const onSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    trigger('name');
    trigger('trigger_value');

    if (!isValid) {
      return;
    }

    const data = getValues();

    if (selectedWorkflow) {
      onWorkflowsChange({ ...selectedWorkflow, ...data }, selectedWorkflow.index);
    } else {
      onWorkflowAdd(data);
    }

    setIsEditWorkflowMode(false);
    setSelectedWorkflow(null);
  };

  return (
    <div className={clsx('flex gap-2.5 rounded-sm-5', compact ? 'flex-col' : 'flex-row')}>
      <div
        className={clsx(
          'flex flex-row font-bold text-sm justify-between px-0 min-h-[34px]',
          !compact ? 'min-w-[200px] items-start py-3.5' : 'items-center',
        )}
      >
        <div className={'flex flex-row gap-2 items-center justify-between w-full'}>
          <div className={'flex flex-row gap-3 items-center'}>
            <p className={'text-purple_750'}>
              {selectedWorkflow ? `Edit custom workflow` : 'New custom workflow'}
            </p>
            <Controller
              control={control}
              name={'enabled'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <div className={'flex flex-row gap-2 items-center'}>
                  <Switch
                    id="wfEnabled"
                    aria-labelledby="wfEnabledLabel"
                    defaultChecked={value}
                    checked={value}
                    onCheckedChange={onChange}
                  />
                  <label
                    id="wfEnabledLabel"
                    htmlFor="wfEnabled"
                    className={'cursor-pointer text-grey_450 text-xs font-medium'}
                  >
                    Enable after saving
                  </label>
                </div>
              )}
            />
          </div>
          <div className={'flex flex-row gap-2'}>
            <Button
              variant="outline"
              type="button"
              className="min-w-24 flex flex-fow gap-2 text-ebony_opacity_80 text-base-xs"
              onClick={onDiscard}
            >
              <span>Discard</span>
            </Button>
            <Button className="min-w-24" onClick={onSave}>
              <span>Save</span>
            </Button>
          </div>
        </div>
      </div>
      <div className={clsx('flex-row items-start flex w-full gap-2.5')}>
        <div className={'flex flex-col gap-2 w-full'}>
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div>
                <Input
                  autoFocus
                  type={'text'}
                  label={'Workflow name'}
                  placeholder={'Name your workflow'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  onFocus={() => {
                    clearErrors('name');
                  }}
                />
              </div>
            )}
          />
          <Controller
            control={control}
            name="trigger_value"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div>
                <Input
                  label={'Workflow steps'}
                  type={'textarea'}
                  className={'min-h-20 overflow-y-auto !px-3.5 !py-3 rounded-lg'}
                  inputBlockClassName={'!items-start justify-start'}
                  textareaRows={6}
                  textareaFieldSizing={'content'}
                  textareaResizable
                  placeholder={'Describe what triggers the workflow and what AI should do next.'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  onFocus={() => {
                    clearErrors('trigger_value');
                  }}
                />
              </div>
            )}
          />
          {!selectedWorkflow && (
            <TooltipProvider delayDuration={150}>
              <div className={'flex flex-row flex-wrap gap-2'}>
                {WORKFLOW_SUGGESTIONS.map(({ name, steps }) => (
                  <Tooltip key={name}>
                    <TooltipTrigger asChild>
                      <button
                        type={'button'}
                        onClick={() => {
                          setValue('name', name, { shouldValidate: true });
                          setValue('trigger_value', steps, { shouldValidate: true });
                          clearErrors(['name', 'trigger_value']);
                        }}
                        className={
                          'rounded-full border border-grey_750 bg-white px-3 py-2 text-xs font-medium text-grey_750 transition-colors hover:bg-grey_750 hover:text-white'
                        }
                      >
                        {name}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side={'top'}
                      sideOffset={8}
                      className={
                        'max-w-[353px] overflow-visible rounded-lg border-none bg-ebony p-2 text-xs font-medium leading-[1.5] text-[#DAE0E2]'
                      }
                    >
                      {steps}
                      <TooltipArrow width={14} height={8} className={'fill-ebony'} />
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          )}
        </div>
      </div>
    </div>
  );
};
